import '@/TokamapEditor/ux/AreaSidebar/NormalHoverToggle.scss';

import ColorButton                   from '@/TokamapEditor/ux/AreaSidebar/ColorButton';
import Button                        from '@widesk-ui/components/Button';
import MixedSelect                   from '@/TokamapEditor/ux/AreaSidebar/MixedSelect';
import View                          from '@widesk-ui/components/View';
import Field                         from '@/TokamapEditor/ux/AreaSidebar/Field';
import NormalHoverToggle             from '@/TokamapEditor/ux/AreaSidebar/NormalHoverToggle';
import useHoverPreview               from '@/TokamapEditor/helpers/useHoverPreview';
import { type NormalHoverScope }     from '@/TokamapEditor/ux/AreaSidebar/NormalHoverToggle';
import currentEditorStore            from '@/TokamapEditor/stores/currentEditorStore';
import { STROKE_WIDTH_RANGES }       from '@/TokamapEditor/helpers/mapKind';
import { metersToUnit }              from '@/TokamapEditor/helpers/mapKind';
import { unitToMeters }              from '@/TokamapEditor/helpers/mapKind';
import { observer }                  from 'mobx-react-lite';
import { DrawTriggerOptions }        from '@tokamap/core';
import { type Shape }                from '@tokamap/core';
import { type ShapeStyle }           from '@tokamap/core';
import { type ShapePassage }         from '@tokamap/core';
import { shapePassage }              from '@tokamap/core';
import SliderInputNumberFeatureStyle from '@/TokamapEditor/ux/AreaSidebar/SliderInputNumberFeatureStyle';
import SwitchField                   from '@/TokamapEditor/ux/SwitchField';
import { commonValue }               from '@/TokamapEditor/ux/AreaSidebar/multiSelectHelpers';
import React                         from 'react';
import useDesignTokens               from '@/hooks/useDesignTokens';
import { DisconnectOutlined }        from '@ant-design/icons';
import { EditOutlined }              from '@ant-design/icons';
import leftSidebarStore              from '@/TokamapEditor/stores/leftSidebarStore';
import themeStore                    from '@/TokamapEditor/stores/themeStore';
import onboardingStore               from '@/TokamapEditor/stores/onboardingStore';
import type { ThemeStyle }            from '@/TokamapEditor/stores/themeStore';
import ThemeSwatch                   from '@/TokamapEditor/ux/ThemeSwatch';
import ThreeDOptions                 from '@/TokamapEditor/ux/AreaSidebar/ThreeDOptions';
import GroundPickers                 from '@/TokamapEditor/ux/AreaSidebar/GroundPickers';
import CollapsiblePanel              from '@/TokamapEditor/ux/AreaSidebar/CollapsiblePanel';
import HelpTip                       from '@/TokamapEditor/ux/AreaSidebar/HelpTip';
import asSingleHistoryEntry          from '@/TokamapEditor/helpers/asSingleHistoryEntry';
import Segmented                     from '@widesk-ui/Segmented';
import Checkbox                      from '@widesk-ui/components/form/Checkbox';
import { themeStyleLock }            from '@/TokamapEditor/helpers/themeImposed';
import { themeValueLock }            from '@/TokamapEditor/helpers/themeImposed';
import { ThemeLockTag }              from '@/TokamapEditor/ux/AreaSidebar/ThemeLocked';
import { ThemeLockedControl }        from '@/TokamapEditor/ux/AreaSidebar/ThemeLocked';
import { zoomAppearanceOptions }     from '@/TokamapEditor/helpers/zoomAppearance';
import { mercatorToLngLat }          from '@tokamap/core';
import { rectAroundPoints }          from '@tokamap/core';
import { shapesExtentPoints }        from '@tokamap/core';

const STATE_NOTES: Record<NormalHoverScope, string> = {
	normal: `Les couleurs et l'épaisseur ci-dessous s'appliquent à l'état normal.`,
	hover: `Les couleurs et l'épaisseur ci-dessous s'appliquent quand le visiteur survole la forme.`,
};

// Valeurs de `Shape.pathPriority` — voir CLAUDE.md "Itinéraire / pathfinding" (`@tokamap/core`) :
// numérique côté lib (`pathPriorityWeight` décroît en continu avec cette valeur), relibellé ici en
// 3 paliers "attractivité" (Faible/Moyenne/Forte) pour rester compréhensible sans exposer l'échelle
// numérique brute à l'utilisateur.
const PATH_PRIORITY_OPTIONS: { label: string; value: NonNullable<Shape['pathPriority']> | ''; }[] = [
	{ label: 'Aucune', value: '' },
	{ label: 'Faible', value: 1 },
	{ label: 'Moyenne', value: 3 },
	{ label: 'Forte', value: 6 },
];

const AreaThemeSection = observer(() => {
	const [scope, setScope] = React.useState<NormalHoverScope>('normal');

	useHoverPreview(scope === 'hover');

	const themed = currentEditorStore.sidebarShapes.some(shape => !!shape.themeId);

	return (
		<View gap="lg">
			{/* En premier : c'est lui qui décide si les réglages suivants s'appliquent. `|| themed` : si
			    le projet n'a plus de thème mais qu'une forme en référence encore un (thème supprimé), le
			    champ reste affiché — sinon plus aucun moyen de la délier. */}
			{(!!themeStore.themes.length || themed) && <ThemeField />}

			<View gap="md">
				<NormalHoverToggle value={scope} onChange={setScope} help={STATE_NOTES[scope]} />

				<Fields key={scope} hover={scope === 'hover'} />
			</View>

			<WalkableField />
		</View>
	);
});

export default AreaThemeSection;

/**
 * Lie la ou les formes sélectionnées à un thème (`Shape.themeId`). Les réglages que le thème impose
 * restent affichés mais verrouillés, les autres se règlent toujours sur la forme.
 */
const ThemeField = observer(() => {
	const editor = currentEditorStore.get;
	const shapes = currentEditorStore.sidebarShapes;

	if (!shapes.length) return null;

	// Même piège que `pathPriority` plus bas : `commonValue` ne distingue pas "aucune forme n'a de
	// thème" de "mélange", les deux renvoyant `undefined` — d'où le `Set` dédié.
	const mixed = new Set(shapes.map(s => s.themeId)).size > 1;
	// `undefined` et non `''` : seul `undefined` ne correspond à aucune option et laisse le placeholder
	// s'afficher (voir le commentaire de `MixedSelect`).
	const themeId = shapes[0]!.themeId;
	const themed = shapes.some(shape => !!shape.themeId);
	const theme = !mixed && themeId ? themeStore.getTheme(themeId) : undefined;

	const setThemeId = (value: string | undefined) => editor.setShapeThemeId(shapes.map(s => s.id), value);

	return (
		<Field
			label={(
				<>
					Thème
					<HelpTip>
						Ce que le thème impose est verrouillé sur la forme ; le reste se règle ici. Les réglages
						propres à la forme sont conservés et reviennent si vous retirez le thème.
					</HelpTip>
				</>
			)}
		>
			<View gap="xs">
				<MixedSelect
					value={themeId}
					mixed={mixed}
					placeholder="Aucun thème"
					onChange={value => setThemeId(value ? String(value) : undefined)}
					// Plus d'option "Aucun" : délier passe par le bouton ci-dessous, plus explicite qu'une
					// entrée de liste à retrouver parmi les thèmes.
					options={themeStore.themes.map(theme => ({
						label: (
							<View row gap="xs" centerV>
								<ThemeSwatch theme={theme} />

								{theme.name}
							</View>
						),
						value: theme.id,
					}))}
				/>

				{themed && (
					// Liens plutôt que boutons bordés, mais AVEC l'icône : seule, la mention textuelle passait
					// inaperçue. `padding: 0` pour que les textes finissent au ras des bords du select.
					<View row spread centerV>
						{theme ? (
							<Button
								type="link"
								size="small"
								icon={<EditOutlined />}
								style={{ padding: 0, height: 'auto' }}
								onClick={() => leftSidebarStore.openThemeForm(theme, { fromShape: true })}
							>
								Modifier le thème
							</Button>
						) : <span />}

						<Button
							type="link"
							size="small"
							icon={<DisconnectOutlined />}
							style={{ padding: 0, height: 'auto' }}
							onClick={() => setThemeId(undefined)}
						>
							Retirer le thème
						</Button>
					</View>
				)}
			</View>
		</Field>
	);
});

const WalkableField = observer(() => {
	const tokens = useDesignTokens();
	const editor = currentEditorStore.get;
	const shapes = currentEditorStore.sidebarShapes;

	if (!shapes.length) return null;

	const passage = commonValue(shapes, shapePassage);
	const mixed = passage === undefined;
	const blocksPassage = mixed ? undefined : passage !== 'free';
	// Une ligne, un cercle ou une image n'ont pas de murs à eux : seul un polygone peut en avoir.
	const canHaveWalls = shapes.every(s => !s.shapeKind || s.shapeKind === 'polygon' || s.shapeKind === 'rectangle' || s.shapeKind === 'circle');

	const locked = commonValue(shapes, s => !!s.locked);
	const lockedMixed = locked === undefined;

	const setLocked = (checked: boolean) => {
		editor.setShapeLocked(shapes.map(s => s.id), checked);
	};

	// `commonValue` ne distingue pas "toutes les formes ont `pathPriority` absent" de "mélange" (les
	// deux renvoient `undefined`) — même piège que `isMixed` plus bas pour `hoverStyle` : vérification
	// dédiée via un `Set` des valeurs réellement présentes.
	const pathPriorityMixed = new Set(shapes.map(s => s.pathPriority)).size > 1;
	const pathPriority = shapes[0]!.pathPriority ?? '';

	const setPathPriority = (value: unknown) => {
		editor.setShapePathPriority(shapes.map(s => s.id), (value === '' || value === null || value === undefined ? undefined : value) as Shape['pathPriority']);
	};

	return (
		<CollapsiblePanel title="Options avancées">
			<Field
				label={(
					<>
						Passage
						<HelpTip>
							Comment les itinéraires piétons passent la forme. « Libre » : on la traverse. « Bloqué » : on la
							contourne. « Par les ouvertures » : ses murs arrêtent, on y entre par ses ouvertures (outil
							Ouverture, touche O) et on circule à l'intérieur.
						</HelpTip>
					</>
				)}
			>
				<MixedSelect
					value={passage}
					mixed={mixed}
					onChange={value => editor.setShapePassage(shapes.map(s => s.id), value as ShapePassage)}
					options={[
						{ value: 'free', label: 'Libre' },
						{ value: 'blocked', label: 'Bloqué' },
						{ value: 'walls', label: 'Par les ouvertures', disabled: !canHaveWalls },
					]}
				/>
			</Field>

			<Field
				label={(
					<>
						Attractivité
						<HelpTip>
							{mixed ? (
							'Incite les itinéraires piétons à passer par cette forme, ou à la longer si elle bloque le passage (mélangé sur la sélection actuelle) — plus l\'attractivité est forte, plus le détour accepté pour ça est grand.'
						) : blocksPassage ? (
							'Cette forme reste infranchissable, mais les itinéraires piétons la longeront plutôt que de s\'en éloigner (plus l\'attractivité est forte, plus le détour accepté pour ça est grand).'
						) : (
								'Les itinéraires piétons seront incités à passer par cette forme (plus l\'attractivité est forte, plus le détour accepté pour ça est grand).'
							)}
						</HelpTip>
					</>
				)}
			>
				<MixedSelect
					value={pathPriority}
					mixed={pathPriorityMixed}
					onChange={setPathPriority}
					options={PATH_PRIORITY_OPTIONS}
				/>
			</Field>

			<MinZoomOffsetField />

			<GroundField />

			<View h={1} bg={tokens.border.subtle} />

			<SwitchField
				label={<>Verrouillée<HelpTip>La forme ne peut plus être déplacée, copiée ou supprimée.</HelpTip></>}
				value={!lockedMixed && !!locked}
				mixed={lockedMixed}
				onChange={setLocked}
			/>
		</CollapsiblePanel>
	);
});

const MinZoomOffsetField = observer(() => {
	const editor = currentEditorStore.get;
	const shapes = currentEditorStore.sidebarShapes;
	const referenceZoom = editor.mapAdapter.getStartCamera()?.zoom ?? currentEditorStore.documentOrigin?.zoom;

	const lock = themeValueLock(shapes)('minZoomOffset');
	const mixed = !lock && new Set(shapes.map(s => s.minZoomOffset)).size > 1;
	const offset = lock ? lock.style.minZoomOffset : shapes[0]!.minZoomOffset;

	const showAppearanceZoom = (minZoomOffset: number | undefined) => {
		if (referenceZoom === undefined) return;

		const rect = rectAroundPoints(shapesExtentPoints(shapes), 0);

		editor.mapAdapter.animateToView({
			zoom: referenceZoom + (minZoomOffset ?? 0),
			...(rect ? { center: mercatorToLngLat(rect.center) } : {}),
		});
	};

	return (
		<Field
			lockedBy={lock}
			label={(
				<>
					Apparition au zoom
					<HelpTip>
						La forme n'apparaît qu'une fois la carte zoomée d'autant depuis sa vue de départ, dans l'aperçu
						et la carte publiée. Dans l'éditeur, elle reste affichée mais estompée tant que ce zoom n'est
						pas atteint.
					</HelpTip>
				</>
			)}
		>
			<MixedSelect
				value={offset ?? ''}
				mixed={mixed}
				options={zoomAppearanceOptions(offset)}
				onChange={value => {
					const minZoomOffset = typeof value === 'number' ? value : undefined;

					editor.setShapeMinZoomOffset(shapes.map(s => s.id), minZoomOffset);
					showAppearanceZoom(minZoomOffset);
				}}
			/>
		</Field>
	);
});

const Fields = observer(({ hover }: { hover?: boolean; }) => {
	const editor = currentEditorStore.get;
	const tokens = useDesignTokens();

	const shapes = currentEditorStore.sidebarShapes;
	const isLine = shapes.some(s => s.shapeKind === 'line');

	const apply = async (
		name: string,
		v: string | number | boolean | undefined,
		options?: DrawTriggerOptions,
		remove = false,
	) => {
		const method = hover ? 'setShapeHoverStyle' : 'setShapeStyle';
		const value: ShapeStyle = remove ? {
			bgColor: undefined,
			borderColor: undefined,
			borderWidth: undefined,
			borderWidthUnit: undefined,
			borderDashed: undefined,
		} : { [name]: v };

		const ids = shapes.map(s => s.id);

		if (!remove) return editor[method](ids, value, options);

		// Réinitialiser le survol rend aussi ses hauteurs à celles du repos, en un seul Annuler.
		await asSingleHistoryEntry(() => {
			editor[method](ids, value, options);
			editor.setShapeHoverHeight(ids, undefined, options);
			editor.setShapeHoverElevation(ids, undefined, options);
		});
	};

	const styleKey = hover ? 'hoverStyle' : 'style';
	const lock = themeStyleLock(shapes, !!hover);
	const lockedBy = (key: keyof ThemeStyle) => lock(key);
	const widthLock = lock('borderWidth');

	const style = {
		borderColor: lock('borderColor')?.style.borderColor ?? commonValue(shapes, s => s[styleKey]?.borderColor),
		bgColor: lock('bgColor')?.style.bgColor ?? commonValue(shapes, s => s[styleKey]?.bgColor),
		borderWidth: widthLock ? widthLock.style.borderWidth : commonValue(shapes, s => s[styleKey]?.borderWidth),
		borderWidthUnit: widthLock ? (widthLock.style.borderWidthUnit ?? 'px') : commonValue(shapes, s => s[styleKey]?.borderWidthUnit ?? 'px'),
		borderDashed: lock('borderDashed') ? lock('borderDashed')!.style.borderDashed : commonValue(shapes, s => s[styleKey]?.borderDashed),
	};

	// `undefined` (mixte) est distingué de "non défini pour toutes les formes" (`false`) : une forme
	// qui n'a jamais eu de style au survol a `hoverStyle` absent partout, donc `commonValue` renvoie
	// `undefined` pour CHAQUE propriété dans ce cas précis — indiscernable d'un vrai mélange sans
	// cette vérification dédiée.
	const isMixed = (key: 'borderColor' | 'bgColor' | 'borderWidth' | 'borderDashed') => {
		if (shapes.length < 2 || lockedBy(key)) return false;
		const values = shapes.map(s => s[styleKey]?.[key]);
		return new Set(values).size > 1;
	};

	const isCreated = !!hover && (
		[style.borderColor, style.bgColor, style.borderWidth, style.borderDashed].some(Boolean)
		|| shapes.some(s => s.hoverHeight !== undefined || s.hoverElevation !== undefined)
	);
	const inMeters = style.borderWidthUnit === 'm';
	const lengthUnit = currentEditorStore.lengthUnit;

	return (
		<View gap="lg" className={hover ? 'normal-hover-fields--hover' : 'normal-hover-fields--normal'}>
			{hover && isCreated && (
				<View row spread centerV>
					<View color={tokens.text.muted} size={12}>Style au survol personnalisé</View>

					<Button size="small" onClick={() => apply('borderColor', '', {}, true)}>
						Réinitialiser
					</Button>
				</View>
			)}

			<Field label="Couleurs">
				<View row gap={6} wrap>
					{!isLine && (
						<span className={hover ? undefined : onboardingStore.targetClass('color')} style={{ display: 'inline-flex' }}>
							<ColorButton
								label="Fond"
								alpha
								lockedBy={lockedBy('bgColor')}
								value={style?.bgColor}
								mixed={isMixed('bgColor')}
								onChange={color => apply('bgColor', color)}
							/>
						</span>
					)}

					<ColorButton
						label="Bordure"
						alpha
						lockedBy={lockedBy('borderColor')}
						value={style?.borderColor}
						mixed={isMixed('borderColor')}
						onChange={color => apply('borderColor', color)}
					/>
				</View>
			</Field>

			{!hover && <GroundPickers shapes={shapes} />}

			<Field label="Épaisseur de bordure" lockedBy={widthLock}>
				<SliderInputNumberFeatureStyle
					property="borderWidth"
					initialValue={style?.borderWidth || 1}
					toModelValue={v => (inMeters ? unitToMeters(v, lengthUnit) : v)}
					fromModelValue={v => (inMeters ? metersToUnit(v, lengthUnit) : v)}
					apply={apply}
					mixed={isMixed('borderWidth')}
					inputProps={inMeters ? STROKE_WIDTH_RANGES[lengthUnit] : { min: 1, max: 20 }}
					unitSelect={{
						value: inMeters ? 'm' : 'px',
						options: [
							{ value: 'px', label: 'px' },
							{ value: 'm', label: lengthUnit },
						],
						title: `px : même épaisseur à l'écran, quel que soit le zoom. ${lengthUnit} : épaisseur au sol, qui suit le zoom.`,
						// Nouvelle unité, épaisseur repartie de 1 : garder le chiffre n'aurait pas de sens.
						onChange: unit => editor[hover ? 'setShapeHoverStyle' : 'setShapeStyle'](
							shapes.map(s => s.id),
							{ borderWidthUnit: unit === 'm' ? 'm' : undefined, borderWidth: unit === 'm' ? unitToMeters(1, lengthUnit) : 1 },
						),
					}}
				/>
			</Field>

			<View row spread centerV>
				<View row centerV gap={6}>
					<View size={13} style={{ fontWeight: 600 }}>Trait de la bordure</View>
					{!!lockedBy('borderDashed') && <ThemeLockTag lock={lockedBy('borderDashed')!} />}
				</View>

				<ThemeLockedControl lock={lockedBy('borderDashed')}>
					<Segmented
						size="small"
						value={isMixed('borderDashed') ? undefined : (style?.borderDashed ? 'dashed' : 'solid')}
						onChange={value => apply('borderDashed', value === 'dashed' || undefined)}
						options={[
							{ value: 'solid', label: 'Plein' },
							{ value: 'dashed', label: 'Pointillés' },
						]}
					/>
				</ThemeLockedControl>
			</View>

			<ThreeDOptions hover={hover} />
		</View>
	);
});

const GroundField = observer(() => {
	const editor = currentEditorStore.get;
	const shapes = currentEditorStore.sidebarShapes;
	const roads = commonValue(shapes, s => s.ground === true || s.ground === 'water');
	const water = commonValue(shapes, s => s.ground === 'water' || s.ground === 'waterOnly');

	const toggle = (layer: 'roads' | 'water', checked: boolean) => asSingleHistoryEntry(() => {
		const byGround = new Map<Shape['ground'], string[]>();

		for (const shape of shapes) {
			const keepsRoads = layer === 'roads' ? checked : shape.ground === true || shape.ground === 'water';
			const keepsWater = layer === 'water' ? checked : shape.ground === 'water' || shape.ground === 'waterOnly';
			const ground = groundOf(keepsRoads, keepsWater);

			if (ground !== (shape.ground || undefined)) byGround.set(ground, [...(byGround.get(ground) ?? []), shape.id]);
		}

		byGround.forEach((ids, ground) => editor.setShapeGround(ids, ground));
	});

	return (
		<Field label={<>Par-dessus la forme<HelpTip>Ce que le fond de carte garde visible par-dessus la forme, pour colorer une zone au sol.</HelpTip></>}>
			<View gap={4}>
				<Checkbox value={roads} onChange={checked => toggle('roads', !!checked)}>
					Routes et chemins
				</Checkbox>

				<Checkbox value={water} onChange={checked => toggle('water', !!checked)}>
					Eau
				</Checkbox>
			</View>
		</Field>
	);
});

function groundOf(roads: boolean, water: boolean): Shape['ground'] {
	if (roads && water) return 'water';
	if (water) return 'waterOnly';

	return roads || undefined;
}
